/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { Edit, Delete } from "@mui/icons-material";
import { Box, Typography, IconButton } from "@mui/material";

import Panel from "../../../../../components/Panel";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { BiometricDeviceStatus } from "../../../../../constants/biometricDevice";
import { IBiometricDevice } from "../../../../../interfaces/BiometricDeviceInterface";
import { useDeleteBiometricDevice } from "../../../../../hooks/query/useBiometricDevices";

interface BiometricDeviceInfoPanelProps {
  device: IBiometricDevice;
  onClose: () => void;
  onEdit: (device: IBiometricDevice) => void;
}

function DetailRow({ label, value, sx }: { label: string; value: string | number; sx?: any }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline" }}>
      <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
        {label}
      </Typography>
      <Typography sx={sx}>{value}</Typography>
    </Box>
  );
}

// eslint-disable-next-line import/prefer-default-export
export function BiometricDeviceInfoPanel({
  device,
  onClose,
  onEdit
}: BiometricDeviceInfoPanelProps): React.JSX.Element {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { mutate: deleteDeviceMutation } = useDeleteBiometricDevice();

  const handleDelete = () => {
    deleteDeviceMutation(device.id as number, {
      onSuccess: (response) => {
        if (response.success) {
          onClose();
        }
      }
    });
  };

  const titleJsx = (
    <Box sx={{ width: "100%" }} display="flex" justifyContent="space-between" alignItems="center">
      <Box display="flex" sx={{ alignItems: "center" }}>
        <Typography>
          <Box component="span" fontWeight="600" fontSize="16px" pr={2}>
            Device Information
          </Box>
        </Typography>
      </Box>
      <Box display="flex">
        <IconButton
          sx={{ cursor: "pointer", padding: "5px" }}
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Delete />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "baseline" }} ml="8px">
          <IconButton sx={{ cursor: "pointer", padding: "5px" }} onClick={() => onEdit(device)}>
            <Edit />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Panel titleJsx={titleJsx} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <DetailRow label="Device Name" value={device.name} />
        <DetailRow label="Serial Number" value={device.serialNumber} />
        <DetailRow label="IP Address" value={device.ip} />
        <DetailRow label="Port" value={device.port || "-"} />
        <DetailRow label="Model" value={device.model || "-"} />
        <DetailRow label="Manufacturer" value={device.manufacturer || "-"} />
        <DetailRow label="Location" value={device.location || "-"} />
        <DetailRow label="Device Type" value={device.deviceType || "-"} />
        <DetailRow
          label="Online Status"
          value={device.isOnline ? "Online" : "Offline"}
          sx={{ color: device.isOnline ? "success.main" : "text.secondary" }}
        />
        <DetailRow
          label="Status"
          value={device.status}
          sx={{
            color: device.status === BiometricDeviceStatus.ACTIVE ? "success.main" : "error.main",
            textTransform: "capitalize"
          }}
        />
        <DetailRow label="Description" value={device?.description || "-"} />
      </Box>

      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Device"
          description={`Are you sure you want to delete the device "${device.name}"?`}
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(false)}
          readMode={false}
        />
      )}
    </Panel>
  );
}
